import React, { useRef, useEffect } from 'react';

const StudentBehaviorDisplay = ({ behaviorData, studentName, viewMode = 'student', colorful = false }) => {
  const canvasRef = useRef(null);

  const categories = [
    { key: 'disciplineRules', label: 'Discipline / Rules Follow' },
    { key: 'classParticipation', label: 'Class Participation' },
    { key: 'respectfulness', label: 'Respect towards Teachers and Students' },
    { key: 'homeworkCompletion', label: 'Homework Completion' },
    { key: 'selfControl', label: 'Self-Control & Emotional Behaviour' }
  ];

  const getScoreColor = (score) => {
    if (score <= 2) return '#ef4444';
    if (score === 3) return '#eab308';
    if (score === 4) return '#3b82f6';
    return '#22c55e';
  };

  const getScoreLabel = (score) => {
    if (score <= 2) return 'Needs Improvement';
    if (score === 3) return 'Satisfactory';
    if (score === 4) return 'Good';
    return 'Excellent';
  };

  const drawRadarChart = () => {
    const canvas = canvasRef.current;
    if (!canvas || !behaviorData) return;

    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 100;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate total and angles for pie chart
    const total = Object.values(behaviorData).reduce((a, b) => a + b, 0);
    let currentAngle = -Math.PI / 2;

    // Draw pie slices
    categories.forEach((category, index) => {
      const value = behaviorData[category.key] || 3;
      const sliceAngle = (value / total) * 2 * Math.PI;
      
      // Draw slice
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
      ctx.closePath();
      
      ctx.fillStyle = getScoreColor(value);
      ctx.fill();
      
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Draw label with value
      const labelAngle = currentAngle + sliceAngle / 2;
      const labelRadius = radius + 40;
      const labelX = centerX + Math.cos(labelAngle) * labelRadius;
      const labelY = centerY + Math.sin(labelAngle) * labelRadius;
      
      ctx.fillStyle = getScoreColor(value);
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`${value}/5`, labelX, labelY - 8);
      
      ctx.font = 'bold 10px Arial';
      ctx.fillStyle = '#2d3748';
      const words = category.label.split(' ');
      if (words.length > 2) {
        ctx.fillText(words.slice(0, 2).join(' '), labelX, labelY + 6);
        ctx.fillText(words.slice(2).join(' '), labelX, labelY + 18);
      } else {
        ctx.fillText(category.label, labelX, labelY + 6);
      }
      
      currentAngle += sliceAngle;
    });

    // Center circle with overall score
    ctx.beginPath();
    ctx.arc(centerX, centerY, 35, 0, 2 * Math.PI);
    const centerGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 35);
    centerGradient.addColorStop(0, '#ffffff');
    centerGradient.addColorStop(1, '#f7fafc');
    ctx.fillStyle = centerGradient;
    ctx.fill();
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    ctx.fillStyle = '#2d3748';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`${total}`, centerX, centerY - 2);
    ctx.font = 'bold 10px Arial';
    ctx.fillText('/ 25', centerX, centerY + 12);
  };

  useEffect(() => {
    drawRadarChart();
  }, [behaviorData]);

  if (!behaviorData) {
    return (
      <div style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
        <h3 style={{ color: '#374151', marginBottom: '8px', fontSize: '16px' }}>No Behavior Evaluation Available</h3>
        <p style={{ color: '#6b7280', fontSize: '14px' }}>Evaluation will appear here once completed</p>
      </div>
    );
  }

  const overallScore = Object.values(behaviorData).reduce((a, b) => a + b, 0);
  const averageScore = overallScore / 5;

  return (
    <div style={{
      background: colorful ? 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' : '#ffffff',
      border: colorful ? '2px solid #0ea5e9' : '1px solid #e5e7eb',
      borderRadius: colorful ? '15px' : '8px',
      padding: '20px',
      boxShadow: colorful ? '0 10px 30px rgba(14, 165, 233, 0.15)' : 'none'
    }}>
      <div style={{
        textAlign: 'center',
        marginBottom: '20px',
        padding: '12px',
        background: '#f8fafc',
        borderRadius: '6px',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', color: '#374151' }}>📊 Behavior Evaluation</h3>
        <p style={{ margin: '0', fontSize: '12px', color: '#6b7280' }}>
          {viewMode === 'teacher' ? `Student: ${studentName}` : 'My Behavior Report'} • {new Date().toLocaleDateString()}
        </p>
      </div>

      <div style={{ 
        display: window.innerWidth <= 768 ? 'block' : 'grid', 
        gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 250px', 
        gap: '20px' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <canvas
            ref={canvasRef}
            width={window.innerWidth <= 768 ? 250 : 300}
            height={window.innerWidth <= 768 ? 250 : 300}
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              background: '#fafafa',
              maxWidth: '100%'
            }}
          />
          <div style={{ marginTop: '10px', fontSize: '11px', color: '#6b7280' }}>
            {categories.map((cat, i) => (
              <div key={i} style={{ marginBottom: '2px' }}>
                {i + 1}. {cat.label}
              </div>
            ))}
          </div>
        </div>

        <div>
          <div style={{
            background: '#f8fafc',
            padding: '15px',
            borderRadius: '6px',
            textAlign: 'center',
            marginBottom: '15px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: getScoreColor(averageScore) }}>
              {averageScore.toFixed(1)}/5
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {getScoreLabel(Math.round(averageScore))}
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <h4 style={{ color: '#374151', marginBottom: '10px', fontSize: '14px' }}>Scores:</h4>
            {categories.map((category) => {
              const score = behaviorData[category.key] || 3;
              return (
                <div key={category.key} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 10px',
                  marginBottom: '4px',
                  background: '#f8fafc',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}>
                  <span style={{ color: '#374151' }}>
                    {category.label.split(' ').slice(0, 2).join(' ')}
                  </span>
                  <div style={{
                    background: getScoreColor(score),
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 'bold'
                  }}>
                    {score}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{
            background: '#f8fafc',
            padding: '10px',
            borderRadius: '4px',
            fontSize: '11px'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#374151' }}>Scale:</div>
            <div style={{ color: '#ef4444' }}>1-2: Needs Improvement</div>
            <div style={{ color: '#eab308' }}>3: Satisfactory</div>
            <div style={{ color: '#3b82f6' }}>4: Good</div>
            <div style={{ color: '#22c55e' }}>5: Excellent</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentBehaviorDisplay;